/**
 * Admin Organisations page — B2B org management (b2b-1-1)
 *
 * Features:
 * - Org list with status badges
 * - Org detail with members tab + agreements tab
 * - Agreement form with live LLM cost estimate
 * - Send / countersign actions
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { tradingApi } from '../../services/tradingApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgResponse {
  id: number;
  name: string;
  slug: string | null;
  status: string;
  domain_auto_join: string;
  kyc_status: string;
  member_count: number;
  active_agreement_id: number | null;
  created_at: string | null;
}

interface EntitlementOverrideResponse {
  id: number;
  org_id: number;
  allowed_exchanges: string[] | null;
  max_watchlists: number | null;
  max_watchlist_items: number | null;
  max_alerts: number | null;
  max_ea_connections: number | null;
  max_ai_requests_per_day: number | null;
  banker_max_daily_leads: number | null;
  feature_overrides: Record<string, boolean> | null;
  notes: string | null;
  created_by_admin_id: number;
  created_at: string | null;
  updated_at: string | null;
}

interface AgreementResponse {
  id: number;
  org_id: number;
  tier: string;
  monthly_price_usd: string;
  billing_model: string;
  max_seats: number | null;
  banker_daily_leads_per_user: number;
  mcp_access_enabled: boolean;
  mcp_llm_calls_per_day: number;
  status: string;
  fm_approved_by: number | null;
  fm_approved_at: string | null;
  marome_countersigned_by: number | null;
  marome_countersigned_at: string | null;
  agreement_start: string;
  agreement_end: string | null;
  auto_renew: boolean;
  notes: string | null;
  estimated_monthly_llm_cost: string | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Status badge helpers
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-700 text-gray-200',
  active: 'bg-green-700 text-green-100',
  suspended: 'bg-yellow-700 text-yellow-100',
  terminated: 'bg-red-800 text-red-100',
  sent: 'bg-blue-700 text-blue-100',
  fm_approved: 'bg-purple-700 text-purple-100',
  expired: 'bg-gray-600 text-gray-200',
  disputed: 'bg-orange-700 text-orange-100',
  incomplete: 'bg-gray-600 text-gray-200',
  pending: 'bg-yellow-700 text-yellow-100',
  approved: 'bg-green-700 text-green-100',
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLORS[status] ?? 'bg-gray-600 text-gray-200';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Per-lead LLM cost constant (matches backend AC:11)
// ---------------------------------------------------------------------------
const PER_LEAD_LLM_COST = 0.0014;

function estimateMonthlyCost(maxSeats: number | null, leadsPerUser: number): string | null {
  if (!maxSeats || maxSeats <= 0) return null;
  const cost = maxSeats * leadsPerUser * 30 * PER_LEAD_LLM_COST;
  return `$${cost.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Agreement Form
// ---------------------------------------------------------------------------

interface AgreementFormProps {
  orgId: number;
  existing?: AgreementResponse;
  onClose: () => void;
}

function AgreementForm({ orgId, existing, onClose }: AgreementFormProps) {
  const queryClient = useQueryClient();

  const [tier, setTier] = useState(existing?.tier ?? 'pro');
  const [monthlyPrice, setMonthlyPrice] = useState(existing?.monthly_price_usd ?? '');
  const [billingModel, setBillingModel] = useState(existing?.billing_model ?? 'per_seat');
  const [maxSeats, setMaxSeats] = useState<number | ''>(existing?.max_seats ?? '');
  const [leadsPerUser, setLeadsPerUser] = useState(existing?.banker_daily_leads_per_user ?? 5);
  const [mcpEnabled, setMcpEnabled] = useState(existing?.mcp_access_enabled ?? false);
  const [mcpCalls, setMcpCalls] = useState(existing?.mcp_llm_calls_per_day ?? 100);
  const [startDate, setStartDate] = useState(existing?.agreement_start ?? '');
  const [endDate, setEndDate] = useState(existing?.agreement_end ?? '');
  const [autoRenew, setAutoRenew] = useState(existing?.auto_renew ?? true);
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const estCost = estimateMonthlyCost(maxSeats === '' ? null : maxSeats, leadsPerUser);

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      tradingApi.post(`/admin/orgs/${orgId}/agreements`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orgs-agreements', orgId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      tradingApi.put(`/admin/orgs/${orgId}/agreements/${existing!.id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orgs-agreements', orgId] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      tier,
      monthly_price_usd: monthlyPrice,
      billing_model: billingModel,
      max_seats: maxSeats === '' ? null : maxSeats,
      banker_daily_leads_per_user: leadsPerUser,
      mcp_access_enabled: mcpEnabled,
      mcp_llm_calls_per_day: mcpCalls,
      agreement_start: startDate,
      agreement_end: endDate || null,
      auto_renew: autoRenew,
      notes: notes || null,
    };
    if (existing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-gray-800 p-4 rounded">
      <h3 className="text-white font-semibold">
        {existing ? 'Edit Agreement' : 'New Agreement'}
      </h3>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Tier</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
          >
            <option value="pro">Pro</option>
            <option value="premium">Premium</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Monthly Price (USD)</label>
          <input
            type="number"
            value={monthlyPrice}
            onChange={(e) => setMonthlyPrice(e.target.value)}
            required
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            step="0.01"
            min="0"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Billing Model</label>
          <select
            value={billingModel}
            onChange={(e) => setBillingModel(e.target.value)}
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
          >
            <option value="per_seat">Per Seat</option>
            <option value="flat">Flat Rate</option>
            <option
              value="revenue_share_pct"
              disabled
              title="Requires legal sign-off — contact my investments compliance"
            >
              Revenue Share % (locked)
            </option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Max Seats</label>
          <input
            type="number"
            value={maxSeats}
            onChange={(e) => setMaxSeats(e.target.value === '' ? '' : parseInt(e.target.value))}
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Banker Leads/User/Day</label>
          <input
            type="number"
            value={leadsPerUser}
            onChange={(e) => setLeadsPerUser(parseInt(e.target.value))}
            required
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">MCP LLM Calls/Day</label>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={mcpEnabled}
              onChange={(e) => setMcpEnabled(e.target.checked)}
            />
            <span className="text-xs text-gray-300">Enable MCP</span>
          </div>
          {mcpEnabled && (
            <input
              type="number"
              value={mcpCalls}
              onChange={(e) => setMcpCalls(parseInt(e.target.value))}
              className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1 mt-1"
              min="1"
            />
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">End Date (optional)</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={autoRenew}
          onChange={(e) => setAutoRenew(e.target.checked)}
        />
        <span className="text-xs text-gray-300">Auto Renew</span>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
        />
      </div>

      {/* Live cost estimate (AC:11) */}
      {estCost && (
        <div className="bg-gray-750 border border-gray-600 rounded p-2 text-sm text-yellow-300">
          Estimated monthly LLM cost: ~{estCost}
        </div>
      )}

      {error && (
        <p className="text-red-400 text-xs">{(error as Error).message}</p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-gray-300 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 disabled:opacity-50"
        >
          {isPending ? 'Saving…' : existing ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Valid exchange codes
// ---------------------------------------------------------------------------

const ALL_EXCHANGES = [
  'JSE', 'FOREX', 'CC', 'INDX',
  'US', 'NYSE', 'NASDAQ', 'LSE', 'XETRA', 'EURONEXT', 'TSX', 'ASX',
];

// ---------------------------------------------------------------------------
// Entitlement Override Panel
// ---------------------------------------------------------------------------

function EntitlementOverridePanel({ orgId }: { orgId: number }) {
  const queryClient = useQueryClient();

  const { data: override, isLoading, error } = useQuery<EntitlementOverrideResponse | null>({
    queryKey: ['admin-org-entitlement', orgId],
    queryFn: async () => {
      try {
        const r = await tradingApi.get(`/admin/orgs/${orgId}/entitlement`);
        return r.data as EntitlementOverrideResponse;
      } catch (e: unknown) {
        if (e && typeof e === 'object' && 'response' in e) {
          const err = e as { response?: { status?: number } };
          if (err.response?.status === 404) return null;
        }
        throw e;
      }
    },
  });

  const [editing, setEditing] = useState(false);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([]);
  const [maxWatchlists, setMaxWatchlists] = useState<string>('');
  const [maxWatchlistItems, setMaxWatchlistItems] = useState<string>('');
  const [maxAlerts, setMaxAlerts] = useState<string>('');
  const [maxEaConnections, setMaxEaConnections] = useState<string>('');
  const [maxAiRequests, setMaxAiRequests] = useState<string>('');
  const [bankerMaxLeads, setBankerMaxLeads] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const startEditing = (existing: EntitlementOverrideResponse | null) => {
    setSelectedExchanges(existing?.allowed_exchanges ?? []);
    setMaxWatchlists(existing?.max_watchlists !== null && existing?.max_watchlists !== undefined ? String(existing.max_watchlists) : '');
    setMaxWatchlistItems(existing?.max_watchlist_items !== null && existing?.max_watchlist_items !== undefined ? String(existing.max_watchlist_items) : '');
    setMaxAlerts(existing?.max_alerts !== null && existing?.max_alerts !== undefined ? String(existing.max_alerts) : '');
    setMaxEaConnections(existing?.max_ea_connections !== null && existing?.max_ea_connections !== undefined ? String(existing.max_ea_connections) : '');
    setMaxAiRequests(existing?.max_ai_requests_per_day !== null && existing?.max_ai_requests_per_day !== undefined ? String(existing.max_ai_requests_per_day) : '');
    setBankerMaxLeads(existing?.banker_max_daily_leads !== null && existing?.banker_max_daily_leads !== undefined ? String(existing.banker_max_daily_leads) : '');
    setNotes(existing?.notes ?? '');
    setEditing(true);
  };

  const upsertMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      tradingApi.put(`/admin/orgs/${orgId}/entitlement`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-entitlement', orgId] });
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => tradingApi.delete(`/admin/orgs/${orgId}/entitlement`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-entitlement', orgId] });
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const toIntOrNull = (v: string) => v.trim() === '' ? null : parseInt(v, 10);
    upsertMutation.mutate({
      allowed_exchanges: selectedExchanges.length > 0 ? selectedExchanges : null,
      max_watchlists: toIntOrNull(maxWatchlists),
      max_watchlist_items: toIntOrNull(maxWatchlistItems),
      max_alerts: toIntOrNull(maxAlerts),
      max_ea_connections: toIntOrNull(maxEaConnections),
      max_ai_requests_per_day: toIntOrNull(maxAiRequests),
      banker_max_daily_leads: toIntOrNull(bankerMaxLeads),
      feature_overrides: null,
      notes: notes || null,
    });
  };

  const toggleExchange = (ex: string) => {
    setSelectedExchanges((prev) =>
      prev.includes(ex) ? prev.filter((x) => x !== ex) : [...prev, ex]
    );
  };

  if (isLoading) return <p className="text-gray-400 text-sm">Loading entitlement override…</p>;
  if (error) return <p className="text-red-400 text-sm">Error loading override.</p>;

  if (!editing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-medium">Entitlement Override</h3>
          <div className="flex gap-2">
            <button
              onClick={() => startEditing(override ?? null)}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500"
            >
              {override ? 'Edit' : 'Set Override'}
            </button>
            {override && (
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-3 py-1 bg-red-700 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {!override ? (
          <p className="text-gray-400 text-sm">No override set — org members use tier defaults.</p>
        ) : (
          <div className="bg-gray-800 rounded p-3 space-y-2 text-sm">
            <div>
              <span className="text-gray-400">Allowed Exchanges: </span>
              <span className="text-white">
                {override.allowed_exchanges?.join(', ') || <em className="text-gray-500">all tier-allowed</em>}
              </span>
            </div>
            {override.banker_max_daily_leads !== null && (
              <div>
                <span className="text-gray-400">Banker Daily Leads Cap: </span>
                <span className="text-white">{override.banker_max_daily_leads === -1 ? 'unlimited' : override.banker_max_daily_leads}</span>
              </div>
            )}
            {override.max_watchlists !== null && (
              <div>
                <span className="text-gray-400">Max Watchlists: </span>
                <span className="text-white">{override.max_watchlists === -1 ? 'unlimited' : override.max_watchlists}</span>
              </div>
            )}
            {override.max_alerts !== null && (
              <div>
                <span className="text-gray-400">Max Alerts: </span>
                <span className="text-white">{override.max_alerts === -1 ? 'unlimited' : override.max_alerts}</span>
              </div>
            )}
            {override.max_ai_requests_per_day !== null && (
              <div>
                <span className="text-gray-400">Max AI Requests/Day: </span>
                <span className="text-white">{override.max_ai_requests_per_day === -1 ? 'unlimited' : override.max_ai_requests_per_day}</span>
              </div>
            )}
            {override.notes && (
              <p className="text-gray-400 italic text-xs">{override.notes}</p>
            )}
          </div>
        )}

        {deleteMutation.error && (
          <p className="text-red-400 text-xs">{(deleteMutation.error as Error).message}</p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-4 bg-gray-800 p-4 rounded">
      <h3 className="text-white font-semibold">
        {override ? 'Edit Entitlement Override' : 'Set Entitlement Override'}
      </h3>

      <div>
        <label className="block text-xs text-gray-400 mb-1">
          Allowed Exchanges <span className="text-gray-500">(empty = no restriction)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {ALL_EXCHANGES.map((ex) => (
            <label key={ex} className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedExchanges.includes(ex)}
                onChange={() => toggleExchange(ex)}
                className="accent-blue-500"
              />
              <span className="text-sm text-gray-200">{ex}</span>
            </label>
          ))}
        </div>
        {selectedExchanges.length > 0 && (
          <p className="text-xs text-yellow-400 mt-1">
            Members will only see: {selectedExchanges.join(', ')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Banker Daily Leads Cap <span className="text-gray-500">(-1 = unlimited)</span></label>
          <input
            type="number"
            value={bankerMaxLeads}
            onChange={(e) => setBankerMaxLeads(e.target.value)}
            placeholder="(not set)"
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="-1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Max Watchlists <span className="text-gray-500">(-1 = unlimited)</span></label>
          <input
            type="number"
            value={maxWatchlists}
            onChange={(e) => setMaxWatchlists(e.target.value)}
            placeholder="(not set)"
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="-1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Max Watchlist Items <span className="text-gray-500">(-1 = unlimited)</span></label>
          <input
            type="number"
            value={maxWatchlistItems}
            onChange={(e) => setMaxWatchlistItems(e.target.value)}
            placeholder="(not set)"
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="-1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Max Alerts <span className="text-gray-500">(-1 = unlimited)</span></label>
          <input
            type="number"
            value={maxAlerts}
            onChange={(e) => setMaxAlerts(e.target.value)}
            placeholder="(not set)"
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="-1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Max EA Connections <span className="text-gray-500">(-1 = unlimited)</span></label>
          <input
            type="number"
            value={maxEaConnections}
            onChange={(e) => setMaxEaConnections(e.target.value)}
            placeholder="(not set)"
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="-1"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Max AI Requests/Day <span className="text-gray-500">(-1 = unlimited)</span></label>
          <input
            type="number"
            value={maxAiRequests}
            onChange={(e) => setMaxAiRequests(e.target.value)}
            placeholder="(not set)"
            className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
            min="-1"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Notes (internal)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1"
          placeholder="Internal note about this override…"
        />
      </div>

      {upsertMutation.error && (
        <p className="text-red-400 text-xs">{(upsertMutation.error as Error).message}</p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="px-3 py-1 text-sm text-gray-300 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={upsertMutation.isPending}
          className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 disabled:opacity-50"
        >
          {upsertMutation.isPending ? 'Saving…' : 'Save Override'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Org Detail
// ---------------------------------------------------------------------------

interface AdminMemberResponse {
  user_id: number | null;
  email: string;
  name: string;
  roles: string[];
  status: 'added' | 'invited';
}

const ORG_ROLES = ['org_admin', 'finance_manager', 'lead_analyst', 'developer', 'devops_sre', 'member'];

function MembersTab({ orgId }: { orgId: number }) {
  const queryClient = useQueryClient();
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [addError, setAddError] = useState<string | null>(null);

  const { data: members, isLoading } = useQuery<AdminMemberResponse[]>({
    queryKey: ['admin-org-members', orgId],
    queryFn: () => tradingApi.get<AdminMemberResponse[]>(`/admin/orgs/${orgId}/members`).then((r) => r.data),
  });

  const [addSuccess, setAddSuccess] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: (body: { email: string; roles: string[] }) =>
      tradingApi.post<AdminMemberResponse>(`/admin/orgs/${orgId}/members`, body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-members', orgId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
      setAddEmail('');
      setAddRole('member');
      setAddError(null);
      setAddSuccess(
        res.data.status === 'invited'
          ? `Invite sent to ${res.data.email} — they'll join when they register.`
          : `${res.data.name} added as ${res.data.roles.map((r) => r.replace(/_/g, ' ')).join(', ')}.`
      );
      setTimeout(() => setAddSuccess(null), 5000);
    },
    onError: (e: Error) => setAddError(e.message),
  });

  const roleChangeMutation = useMutation({
    mutationFn: ({ userId, roles }: { userId: number; roles: string[] }) =>
      tradingApi.put(`/admin/orgs/${orgId}/members/${userId}`, { roles }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-org-members', orgId] }),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => tradingApi.delete(`/admin/orgs/${orgId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-members', orgId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
    },
  });

  return (
    <div className="space-y-4">
      {/* Add member form */}
      <div className="bg-gray-800 rounded p-3 space-y-2">
        <h3 className="text-white text-sm font-medium">Add Member</h3>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-400 mb-1">Email</label>
            <input
              value={addEmail}
              onChange={(e) => { setAddEmail(e.target.value); setAddError(null); }}
              placeholder="user@company.com"
              className="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Role</label>
            <select
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
              className="bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600"
            >
              {ORG_ROLES.map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => { if (addEmail.trim()) addMutation.mutate({ email: addEmail.trim(), roles: [addRole] }); }}
            disabled={addMutation.isPending || !addEmail.trim()}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 disabled:opacity-50"
          >
            {addMutation.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
        {addError && <p className="text-red-400 text-xs">{addError}</p>}
        {addSuccess && <p className="text-green-400 text-xs">{addSuccess}</p>}
        <p className="text-gray-500 text-xs">If the user doesn't have an account yet, an invite email will be sent automatically.</p>
      </div>

      {/* Member list */}
      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
      {members && members.length === 0 && (
        <p className="text-gray-400 text-sm">No members yet.</p>
      )}
      {members && members.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="py-1.5 pr-4">Name / Email</th>
              <th className="py-1.5 pr-4">Role</th>
              <th className="py-1.5" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id} className="border-b border-gray-800">
                <td className="py-1.5 pr-4">
                  <div className="text-white">{m.name}</div>
                  <div className="text-gray-400 text-xs">{m.email}</div>
                </td>
                <td className="py-1.5 pr-4">
                  <div className="flex flex-wrap gap-0.5 mb-1">
                    {(m.roles || []).map((r) => (
                      <span key={r} className="text-xs bg-gray-600 text-gray-200 rounded px-1 py-0.5">{r.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                  <div className="space-y-0.5">
                    {ORG_ROLES.map((r) => (
                      <label key={r} className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(m.roles || []).includes(r)}
                          onChange={(e) => {
                            const current = m.roles || [];
                            const next = e.target.checked
                              ? [...current, r]
                              : current.filter((x) => x !== r);
                            if (next.length > 0 && m.user_id != null) {
                              roleChangeMutation.mutate({ userId: m.user_id, roles: next });
                            }
                          }}
                          className="accent-blue-500"
                        />
                        {r.replace(/_/g, ' ')}
                      </label>
                    ))}
                  </div>
                </td>
                <td className="py-1.5 text-right">
                  <button
                    onClick={() => { if (m.user_id != null) removeMutation.mutate(m.user_id); }}
                    disabled={removeMutation.isPending || m.user_id == null}
                    className="text-xs text-gray-500 hover:text-red-400 transition-colors disabled:opacity-30"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OrgDetail({ org, onBack }: { org: OrgResponse; onBack: () => void }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'members' | 'agreements' | 'entitlements' | 'kyc'>('members');
  const [showForm, setShowForm] = useState(false);
  const [editAgreement, setEditAgreement] = useState<AgreementResponse | undefined>(undefined);

  const { data: agreements, isLoading: agLoading } = useQuery<AgreementResponse[]>({
    queryKey: ['admin-orgs-agreements', org.id],
    queryFn: () => tradingApi.get<AgreementResponse[]>(`/admin/orgs/${org.id}/agreements`).then((r) => r.data),
  });

  const sendMutation = useMutation({
    mutationFn: (agId: number) =>
      tradingApi.post(`/admin/orgs/${org.id}/agreements/${agId}/send`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-orgs-agreements', org.id] }),
  });

  const countersignMutation = useMutation({
    mutationFn: (agId: number) =>
      tradingApi.post(`/admin/orgs/${org.id}/agreements/${agId}/countersign`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orgs-agreements', org.id] });
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-sm text-blue-400 hover:text-blue-300">
          ← Back
        </button>
        <h2 className="text-white text-lg font-semibold">{org.name}</h2>
        <StatusBadge status={org.status} />
        <span className="text-xs text-gray-400">KYC: <StatusBadge status={org.kyc_status} /></span>
      </div>

      <div className="flex gap-2 border-b border-gray-700 pb-1">
        {(['members', 'agreements', 'entitlements', 'kyc'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`text-sm px-3 py-1 capitalize ${
              tab === t ? 'text-white border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'members' && <MembersTab orgId={org.id} />}

      {tab === 'agreements' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-medium">Pricing Agreements</h3>
            <button
              onClick={() => { setEditAgreement(undefined); setShowForm(true); }}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500"
            >
              + New Agreement
            </button>
          </div>

          {showForm && (
            <AgreementForm
              orgId={org.id}
              existing={editAgreement}
              onClose={() => { setShowForm(false); setEditAgreement(undefined); }}
            />
          )}

          {agLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {agreements?.map((ag) => (
            <div key={ag.id} className="bg-gray-800 rounded p-3 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge status={ag.status} />
                <span className="text-white text-sm font-medium">
                  {ag.tier.toUpperCase()} — ${ag.monthly_price_usd}/mo
                </span>
                <span className="text-gray-400 text-xs">{ag.billing_model}</span>
                {ag.max_seats && (
                  <span className="text-gray-400 text-xs">{ag.max_seats} seats</span>
                )}
                {ag.estimated_monthly_llm_cost && (
                  <span className="text-yellow-400 text-xs">
                    ~{ag.estimated_monthly_llm_cost} LLM cost/mo
                  </span>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                {ag.status === 'draft' && (
                  <>
                    <button
                      onClick={() => { setEditAgreement(ag); setShowForm(true); }}
                      className="px-2 py-0.5 text-xs bg-gray-700 text-gray-200 rounded hover:bg-gray-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => sendMutation.mutate(ag.id)}
                      disabled={sendMutation.isPending}
                      className="px-2 py-0.5 text-xs bg-blue-700 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      Send to FM
                    </button>
                  </>
                )}
                {ag.status === 'fm_approved' && (
                  <button
                    onClick={() => countersignMutation.mutate(ag.id)}
                    disabled={countersignMutation.isPending}
                    className="px-2 py-0.5 text-xs bg-green-700 text-white rounded hover:bg-green-600 disabled:opacity-50"
                  >
                    Countersign (Activate)
                  </button>
                )}
              </div>

              {ag.fm_approved_at && (
                <p className="text-xs text-gray-400">
                  FM approved: {new Date(ag.fm_approved_at).toLocaleDateString()}
                </p>
              )}
              {ag.notes && (
                <p className="text-xs text-gray-400 italic">{ag.notes}</p>
              )}
            </div>
          ))}
          {agreements?.length === 0 && (
            <p className="text-gray-400 text-sm">No agreements yet.</p>
          )}
        </div>
      )}

      {tab === 'entitlements' && (
        <EntitlementOverridePanel orgId={org.id} />
      )}

      {tab === 'kyc' && (
        <div className="text-gray-400 text-sm">
          <p>KYC management is available in the KYC Review section.</p>
          <p className="mt-2 text-xs">Current KYC status: <StatusBadge status={org.kyc_status} /></p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Organisations page
// ---------------------------------------------------------------------------

export default function Organisations() {
  const queryClient = useQueryClient();
  const [selectedOrg, setSelectedOrg] = useState<OrgResponse | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [confirmDeleteOrg, setConfirmDeleteOrg] = useState<OrgResponse | null>(null);

  const { data: orgs, isLoading, error } = useQuery<OrgResponse[]>({
    queryKey: ['admin-orgs'],
    queryFn: () => tradingApi.get<OrgResponse[]>('/admin/orgs').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; slug?: string }) =>
      tradingApi.post('/admin/orgs', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
      setShowCreate(false);
      setCreateName('');
      setCreateSlug('');
      setCreateError(null);
    },
    onError: (e: Error) => setCreateError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (orgId: number) => tradingApi.delete(`/admin/orgs/${orgId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
      setConfirmDeleteOrg(null);
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;
    createMutation.mutate({
      name: createName.trim(),
      ...(createSlug.trim() ? { slug: createSlug.trim() } : {}),
    });
  };

  if (selectedOrg) {
    return (
      <OrgDetail
        org={selectedOrg}
        onBack={() => setSelectedOrg(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-white text-xl font-bold">Organisations</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-500"
        >
          + New Organisation
        </button>
      </div>

      {/* Create org modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <form
            onSubmit={handleCreate}
            className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md space-y-4"
          >
            <h2 className="text-white font-semibold text-lg">New Organisation</h2>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Name *</label>
              <input
                autoFocus
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Acme Capital"
                className="w-full bg-gray-800 text-white text-sm rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Slug <span className="text-gray-600">(optional)</span></label>
              <input
                value={createSlug}
                onChange={(e) => setCreateSlug(e.target.value)}
                placeholder="e.g. acme-capital"
                className="w-full bg-gray-800 text-white text-sm rounded px-3 py-2 border border-gray-700 focus:outline-none focus:border-blue-500"
              />
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowCreate(false); setCreateError(null); }}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || !createName.trim()}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDeleteOrg && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md space-y-4">
            <h2 className="text-white font-semibold text-lg">Delete Organisation</h2>
            <p className="text-gray-300 text-sm">
              Are you sure you want to permanently delete{' '}
              <span className="text-white font-medium">{confirmDeleteOrg.name}</span>?
              This will remove all members, agreements, and related data. This action cannot be undone.
            </p>
            {deleteMutation.error && (
              <p className="text-red-400 text-sm">{(deleteMutation.error as Error).message}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setConfirmDeleteOrg(null); deleteMutation.reset(); }}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(confirmDeleteOrg.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-500 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && <p className="text-gray-400">Loading…</p>}
      {error && <p className="text-red-400">Error loading organisations.</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Slug</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">KYC</th>
              <th className="py-2 pr-4">Members</th>
              <th className="py-2 pr-4">Active Agreement</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {orgs?.map((org) => (
              <tr
                key={org.id}
                className="border-b border-gray-800 hover:bg-gray-800 cursor-pointer"
                onClick={() => setSelectedOrg(org)}
              >
                <td className="py-2 pr-4 text-white font-medium">{org.name}</td>
                <td className="py-2 pr-4 text-gray-400">{org.slug ?? '—'}</td>
                <td className="py-2 pr-4">
                  <StatusBadge status={org.status} />
                </td>
                <td className="py-2 pr-4">
                  <StatusBadge status={org.kyc_status} />
                </td>
                <td className="py-2 pr-4 text-gray-300">{org.member_count}</td>
                <td className="py-2 pr-4 text-gray-400">
                  {org.active_agreement_id ? `#${org.active_agreement_id}` : '—'}
                </td>
                <td className="py-2 text-blue-400 text-xs hover:underline">View →</td>
                <td className="py-2 pl-2" onClick={(e) => e.stopPropagation()}>
                  {org.status === 'draft' && (
                    <button
                      onClick={() => setConfirmDeleteOrg(org)}
                      className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                      title="Delete organisation"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orgs?.length === 0 && (
        <p className="text-gray-400 text-sm">No organisations yet.</p>
      )}
    </div>
  );
}
